class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

// class HeaderMenu extends DetailsDisclosure {
//   constructor() {
//     super();
//     this.header = document.querySelector('.header-wrapper');
//   }

//   onToggle() {
//     if (!this.header) return;
//     this.header.preventHide = this.mainDetailsToggle.open;

//     if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
//     document.documentElement.style.setProperty(
//       '--header-bottom-position-desktop',
//       `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
//     );
//   }
// }

// customElements.define('header-menu', HeaderMenu);

class HeaderMenu extends DetailsDisclosure {
  _isClosing = false;
  _listenerActive = false;
  _mouseTrackingCounter = false;
  _menuStaysOpenForce = false;

  _animation = null;
  _animationKeyframe = null;
  _animationKeys = [
    { transform: 'translateY(-20px)', opacity: 0 },
    { transform: 'translateY(0px)', opacity: 1 }
  ];
  _animationProps = {
    duration: 350,
    easing: 'ease-in-out',
    fill: 'forwards',
  };

  constructor() {
    super();
    this._abortController = new AbortController();
    this._clickHandler = this._handleClick.bind(this);
    this._mouseLeaveHandler = this._handleMouseLeave.bind(this);
    this._mouseEnterHandler = this._handleMouseEnter.bind(this);
    this._documentMouseOverHandler = this._handleDocumentMouseOver.bind(this);
    
    // Create debounced version for menu switching
    this._debouncedMenuSwitchCheck = debounce(this._checkMenuSwitch.bind(this), 100);
    
    this._initAnimation();
  }

  connectedCallback() {
    const { signal } = this._abortController;
    if (this._trigger === 'hover') {
      this.addHoverEventListeners(signal);
    } else {
      this.addClickEventListeners(signal);
    }
  }

  disconnectedCallback() {
    this._abortController.abort();
    this._clearCountdownTimer();
  }

  _initAnimation() {
    if (!this._animate || !this.headerMenuContent) return;
    
    this._animationKeyframe = new KeyframeEffect(
      this.headerMenuContent, 
      this._animationKeys, 
      this._animationProps
    );
    this._animation = new Animation(this._animationKeyframe);
    
    if (this._animation.ready) {
      Object.assign(this.headerMenuContent.style, this._animationKeys[0]);
    }
  }

  _handleClick(event) {
    if (!this.headerMenuSummary.contains(event.target)) {
      return;
    } else {
      event.preventDefault();
    };

    if (!this._open) {
      this.allOtherActiveMenus.length && this.closeAllActiveMenus(this.allOtherActiveMenus);
      this.openMenu();
    } else {
      this.closeMenu();
    }
  }

  addClickEventListeners(signal) {
    this.headerMenu?.addEventListener('click', this._clickHandler, { signal });
  }

  _handleMouseLeave(event) {
    if (!this._open) return;
    
    // Only start timer if we're leaving to outside the menu content
    if (!this.headerMenuContent.contains(event.relatedTarget)) {
      this._toggleDetectionTimer(true);
      this.addDocumentListener();
    }
  }

  _handleMouseEnter(event) {
    // Cancel any pending close timers
    this._toggleDetectionTimer(false);
    
    this.allOtherActiveMenus.length && this.closeAllActiveMenus(this.allOtherActiveMenus);
    
    if (this._isClosing && this._open && this._animation?.playState === 'running') {
      this._animation.pause();
      this._animation.playbackRate = 1;
      this._animation.play();
      this._isClosing = false;
    } else if (!this._open) {
      this.openMenu();
    }
  }

  addDocumentListener() {
    if (this._listenerActive) return;
    
    const { signal } = this._abortController;
    document.addEventListener(
      'mouseover', 
      this._documentMouseOverHandler, 
      { signal, passive: true }
    );
    this._listenerActive = true;
  }

  removeDocumentListener() {
    if (!this._listenerActive) return;
    
    document.removeEventListener('mouseover', this._documentMouseOverHandler);
    this._listenerActive = false;
  }

  _handleDocumentMouseOver(event) {
    if (!this._userSwitchMenu || !this._open) return;
    this._debouncedMenuSwitchCheck(event);
  }

  _checkMenuSwitch(event) {
    if (!this.headerMenu || !event) return;
    
    // Check if mouse is still within the entire menu structure
    const isInMenu = this.headerMenu.contains(event.target);
    
    if (!isInMenu) {
      // Mouse is completely outside - start close timer
      this._toggleDetectionTimer(true);
    }
  }

  addHoverEventListeners(signal) {
    this.headerMenu?.addEventListener('mouseenter', this._mouseEnterHandler, { signal });
    this.headerMenu?.addEventListener('mouseleave', this._mouseLeaveHandler, { signal });
  }

  openMenu(animate = this._animate) {
    if (animate && this._animation) {
      this.open();
      this._animation.playbackRate = 1;
      this._animation.play();
    } else {
      this.open();
    }
  }

  closeMenu(animate = this._animate) {
    if (this._menuStaysOpenForce) return;
    
    this._isClosing = true;
    this.removeDocumentListener();
    this._clearCountdownTimer();
    this.closeAllActiveChildDropdowns();

    if (animate && this._animation) {
      this._animation.playbackRate = -1;
      this._animation.play();
      
      this._animation.finished
        .then(() => {
          if (this._isClosing) this.close();
          this._isClosing = false;
        })
        .catch(() => {});
    } else {
      this.close();
    }
  }

  closeAllActiveMenus(menusToClose) {
    menusToClose?.forEach(menu => {
      if (menu._open) {
        menu.closeMenu();
        menu._listenerActive && menu.removeDocumentListener();
      }
    });
  }

  closeAllActiveChildDropdowns() {
    if (this.menuChildDropdowns.length <= 0) return;

    this.menuActiveChildDropdowns.forEach(childMenu => {
      const childMenuSummary = childMenu.querySelector('summary');
      childMenuSummary.parentElement?.hasAttribute('open') && childMenuSummary?.click();
    });
  }

  onToggle() {
    if (!this.headerWrapper) return;
    this.headerWrapper.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.headerWrapper.getBoundingClientRect().bottom)}px`
    );
  }

  _clearCountdownTimer() {
    clearTimeout(this._mouseTrackingCounter);
    this._mouseTrackingCounter = false;
  }

  _toggleDetectionTimer(toggle = false) {
    if (this._trigger !== "hover") return;
    
    this._clearCountdownTimer();
    
    if (toggle) {
      this._mouseTrackingCounter = setTimeout(() => {
        // Only close if not forcing stay open and still open
        if (!this._menuStaysOpenForce && this._open) {
          this.closeMenu();
        }
      }, 500);
    }
  }

  _toggleForceOpen(force = false) {
    return this._menuStaysOpenForce = force ? force : !this._menuStaysOpenForce;
  }

  open() {
    this.headerMenu?.setAttribute('open', '');
  }

  close() {
    this.headerMenu?.removeAttribute('open');
  }

  get _trigger() {
    return this.getAttribute("trigger");
  }

  get _open() {
    return this.headerMenu?.hasAttribute('open');
  }

  get _animate() {
    return this.headerMenu?.classList.contains('mega-menu');
  }

  get _userSwitchMenu() {
    return this.getAttribute('data-user-switch-menu') === 'true';
  }

  get headerSection() {
    return this.closest('.section-header');
  }

  get headerWrapper() {
    return this.closest('.header-wrapper');
  }

  get allHeaderMenus() {
    return Array.from(this.headerSection?.querySelectorAll('header-menu') || []);
  }

  get allOtherActiveMenus() {
    return this.allHeaderMenus.filter(item => item._open && item !== this);
  }

  get headerMenuList() {
    return this.closest('.list-menu--inline');
  }

  get headerMenu() {
    return this.querySelector('details');
  }

  get headerMenuSummary() {
    return this.headerMenu?.querySelector('summary');
  }

  get headerMenuContent() {
    return this.querySelector('[id^="HeaderMenu-MenuList-"], [id^="MegaMenu-Content-"]');
  }

  get headerMenuContentInner() {
    return this.querySelector('.mega-menu__content-inner');
  }

  get menuChildDropdowns() {
    return Array.from(this.headerMenuContent?.querySelectorAll('details') || []);
  }

  get menuActiveChildDropdowns() {
    return this.menuChildDropdowns.filter(item => item.hasAttribute('open'));
  }
}

customElements.define('header-menu', HeaderMenu);
